import { FC } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Textarea } from '../../../../common/components/Form';
import { Colors } from '../../../../assets';
import { ComplementType, RequestsFormValues } from '../models';
import { Spacing } from '../../../../assets/GlobalStyle/Variables';
import { useReferences } from '../../../../helpers/context_manager/useReferences';
import { CheckboxSkeleton } from '../../../../common/components/Skeleton';

const API_SUPPORTED: ReadonlySet<ComplementType> = new Set([
  'ct',
  'rtg',
  'toxicology',
  'tryptase',
  'microbiology',
  'genetics',
  'biochemistry',
  'histology',
  'nrl_other',
  'nrl_respiratory',
  'transport',
  'virology'
]);

type Props = {
  complementOptions: { type: ComplementType; label: string }[];
  savedComplements: ReadonlySet<string>;
  dirtyComplements: ReadonlySet<string>;
  currentUserName: string;
  isLoading: boolean;
};

export const RequestHeader: FC<Props> = ({
  complementOptions,
  savedComplements,
  dirtyComplements,
  currentUserName,
  isLoading
}) => {
  const {
    watch,
    setValue,
    register
  } = useFormContext<RequestsFormValues>();
  const { references } = useReferences();
  const examinationType = watch('examinationType');
  const selectedComplement = watch('selectedComplement');
  const isOther = selectedComplement === 'other';

  return (
    <Row>
      <Col xl={6} lg={12} md={12}>
        <FieldRow>
          <FieldLabel>Typ vyšetření *</FieldLabel>
          <RadioGroup>
            {references.examinationTypes
              .filter((et) => et.is_active)
              .map((et) => (
                <RadioItem key={et.id}>
                  <input
                    type="radio"
                    checked={examinationType === String(et.id)}
                    onChange={() => setValue('examinationType', String(et.id))}
                  />
                  <span>{et.name}</span>
                </RadioItem>
              ))}
          </RadioGroup>
        </FieldRow>

<FieldRow>
          <FieldLabel>Vytvořil</FieldLabel>
          <span>{currentUserName || '—'}</span>
        </FieldRow>
      </Col>

      <Col xl={6} lg={12} md={12}>
        <SectionTitle>Komplement</SectionTitle>
        <SectionSubTitle>vyberte typ žádanky</SectionSubTitle>

        <ComplementsGrid>
          {isLoading
            ? Array.from({ length: 12 }).map((_, i) => (
                <SkeletonItem key={i}>
                  <CheckboxSkeleton />
                </SkeletonItem>
              ))
            : complementOptions.map((opt) => {
                const disabled = !API_SUPPORTED.has(opt.type);
                const hasSaved = savedComplements.has(opt.type);
                const isDirty = dirtyComplements.has(opt.type);
                return (
                  <ComplementItem key={opt.type} $disabled={disabled}>
                    <input
                      type="radio"
                      value={opt.type}
                      checked={selectedComplement === opt.type}
                      disabled={disabled}
                      {...register('selectedComplement')}
                    />
                    <ItemLabel>
                      <span>{opt.label}</span>
                      {hasSaved && !isDirty && <SavedBadge>✓ uloženo</SavedBadge>}
                      {hasSaved && isDirty && <UnsavedBadge>● neuloženo</UnsavedBadge>}
                    </ItemLabel>
                  </ComplementItem>
                );
              })
          }
        </ComplementsGrid>

        {isOther && (
          <OtherNoteWrapper>
            <Textarea
              id="otherComplementNote"
              name="otherComplementNote"
              label="Upřesnění (ostatní)"
              rows={2}
              required
            />
          </OtherNoteWrapper>
        )}
      </Col>
    </Row>
  );
};

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${Spacing(2)};
`;

const SectionSubTitle = styled.div`
  color: ${Colors.grey};
  margin-bottom: ${Spacing(3)};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(2)};
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;

  input {
    width: 18px;
    height: 18px;
  }
`;

const SkeletonItem = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const ComplementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${Spacing(2)} ${Spacing(4)};
`;

const ComplementItem = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  user-select: none;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'default')};

  input {
    width: 18px;
    height: 18px;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  }
`;

const ItemLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;

  span {
    color: ${Colors.grey};
  }
`;

const SavedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: #d4edda;
  color: #1e7e34;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const UnsavedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: #ffc107;
  color: #000;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing(3)};
  margin-bottom: ${Spacing(3)};
`;

const FieldLabel = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
  width: 140px;
  flex-shrink: 0;
  padding-top: 2px;
`;

const OtherNoteWrapper = styled.div`
  margin-top: ${Spacing(3)};
`;

